import React from 'react';
import { Trans, t } from '@lingui/macro';
import { CircleDot, Download, Send, Square } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@shared/api/api-client';
import { useAuth } from '@shared/hooks/useAuth';
import { diagnosticRecorder } from '@shared/lib/diagnosticRecorder';
import { Button } from '@shared/ui/button';

/**
 * Persistent on-screen indicator + controls while a diagnostic recording is
 * live. Always visible while active so the operating admin (and anyone they
 * screen-share with) knows capture is running.
 */
export function DiagnosticRecorderOverlay() {
  const { user } = useAuth();
  const state = React.useSyncExternalStore(
    diagnosticRecorder.subscribe,
    diagnosticRecorder.getState,
    diagnosticRecorder.getState
  );
  const [sending, setSending] = React.useState(false);
  const isCallStaff = user?.role === 'admin' || user?.role === 'super_admin';

  if (!isCallStaff || !state.active) return null;

  const handleStopAndExport = () => {
    const report = diagnosticRecorder.exportSession();
    diagnosticRecorder.stop();
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `jema-diagnostic-${report.startedAt.replace(/[:.]/g, '-')}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success(
      t({
        id: 'shared.diagnostics.exported',
        message: 'Diagnostic recording exported.',
      })
    );
  };

  const handleSendToSupport = async () => {
    setSending(true);
    try {
      const report = diagnosticRecorder.exportSession();
      await apiClient.post('/diagnostics/session-report', report);
      toast.success(
        t({
          id: 'shared.diagnostics.sent',
          message: 'Diagnostic report sent to support.',
        })
      );
    } catch (error) {
      toast.error(
        error instanceof Error && error.message.trim()
          ? error.message
          : t({
              id: 'shared.diagnostics.sendError',
              message: 'Could not send the diagnostic report. Try again.',
            })
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <aside
      role='status'
      className='fixed bottom-3 left-3 z-[80] flex items-center gap-2 rounded-xl border border-red-500/40 bg-background/95 px-3 py-2 shadow-lg shadow-black/20'
    >
      <CircleDot className='h-4 w-4 animate-pulse text-red-500' aria-hidden='true' />
      <div className='min-w-0'>
        <p className='text-xs font-semibold text-red-600 dark:text-red-400'>
          <Trans id='shared.diagnostics.recording'>Diagnostic recording…</Trans>
        </p>
        <p className='text-xs text-muted-foreground'>
          {state.consoleCount + state.networkCount}{' '}
          <Trans id='shared.diagnostics.entries'>entries captured</Trans>
        </p>
      </div>
      <Button
        type='button'
        size='sm'
        variant='outline'
        className='h-8 gap-1 text-xs'
        onClick={handleStopAndExport}
      >
        <Square className='h-3.5 w-3.5' aria-hidden='true' />
        <Download className='h-3.5 w-3.5' aria-hidden='true' />
        <Trans id='shared.diagnostics.stopExport'>Stop &amp; Export</Trans>
      </Button>
      <Button
        type='button'
        size='sm'
        className='h-8 gap-1 bg-brand-blue text-xs text-white hover:bg-brand-blue/90'
        disabled={sending}
        onClick={() => void handleSendToSupport()}
      >
        <Send className='h-3.5 w-3.5' aria-hidden='true' />
        <Trans id='shared.diagnostics.sendSupport'>Send to support</Trans>
      </Button>
    </aside>
  );
}
